import type { Catalogs, Translation } from "@amtana/i18n/catalog";

// Pure copy for the Agents area (ADR-023 §12): no React, no I/O.

export type AgentCatalog = typeof EN;

const EN = {
  screenTitleList: "Agents · {product}",
  screenTitleAgent: "Agent · {product}",
  navAgents: "Agents",
  breadcrumbHome: "Home",
  agentsTitle: "Agents",
  agentsDescription: "Agents that prospect on LinkedIn and Instagram for you, on a machine we run.",
  newRemoteAgent: "New remote agent",
  tableLabel: "Your agents",
  columnAgent: "Agent",
  columnStatus: "Status",
  columnLastTask: "Last task",
  noAgentsTitle: "No agents yet",
  noAgentsDescription: "Create a remote agent. It stays asleep until you give it a task.",
  noTaskYet: "No task yet",
  hostingRemote: "Remote",
  agentKind: "{hosting} · {harness}",
  harnessClaudeCode: "Your Claude",
  harnessCodex: "Your Codex",
  harnessZaistevModel: "Our model",
  harnessClaudeCodeDescription: "Runs on your own Claude login. The model costs nothing on our side.",
  harnessCodexDescription: "Runs on your own Codex login. The model costs nothing on our side.",
  harnessZaistevModelDescription: "Runs on our model, no login of yours. Each turn draws your AI credits.",
  eventKindModel: "Our model",
  eventModelAnswered: "Asked our model",
  eventModelRefused: "Our model refused the call",
  statusAsleep: "Asleep",
  statusWaking: "Waking",
  statusRunning: "Running",
  statusIdle: "Idle",
  statusStopped: "Stopped",
  drawerTitle: "New remote agent",
  drawerDescription: "We keep its record now. Its machine wakes only when you give it a task.",
  name: "Name",
  namePlaceholder: "Scout",
  nameProblem: "Give it a name of up to 60 characters.",
  runsOn: "Runs on",
  ownLoginsOnly: "Our own model will appear here once it is available.",
  create: "Create agent",
  creating: "Creating…",
  cancel: "Cancel",
  agentCreated: "{name} is ready",
  agentCreatedDescription: "It is asleep until you give it a task.",
  couldNotCreate: "Could not create that agent",
  addonRequiredTitle: "Remote agents come with the Remote agent add-on",
  addonRequiredDescription:
    "It gives your agent a machine and {hours} awake hours a month. Get it in the store, then create your agent here.",
  getRemoteAgent: "Get Remote agent",
  tasksTitle: "Tasks",
  tasksDescription: "What this agent was asked to do, and every step it takes.",
  giveTitle: "Give a task",
  giveDescription: "Say what to do in a sentence. The agent wakes, works through it and goes back to sleep.",
  instructionLabel: "Task",
  instructionPlaceholder: "Find 20 SaaS founders in Madrid, add them to Q4",
  instructionProblem: "Write the task in up to 2,000 characters.",
  whenLabel: "When",
  whenNow: "Now",
  whenSchedule: "On a schedule",
  repeatLabel: "Repeats",
  repeatDaily: "Every day",
  repeatWeekdays: "Every weekday",
  repeatMonday: "Every Monday",
  repeatTuesday: "Every Tuesday",
  repeatWednesday: "Every Wednesday",
  repeatThursday: "Every Thursday",
  repeatFriday: "Every Friday",
  repeatSaturday: "Every Saturday",
  repeatSunday: "Every Sunday",
  timeLabel: "At",
  timeProblem: "Pick a time such as 09:00.",
  timeZoneHint: "In the time zone set in your preferences.",
  giveTask: "Give task",
  giving: "Giving…",
  scheduleTask: "Schedule task",
  scheduling: "Scheduling…",
  taskGiven: "{name} is on it",
  taskGivenDescription: "Its machine wakes and each step shows below as it happens.",
  taskScheduledToast: "Task scheduled",
  taskScheduledToastDescription: "It runs next on {when}.",
  couldNotGive: "Could not give that task",
  couldNotSchedule: "Could not schedule that task",
  poolBusyTitle: "The pool is busy",
  poolBusyDescription: "Every machine we run is awake right now. Give the task again in a minute.",
  tasksTableLabel: "This agent's tasks",
  columnTask: "Task",
  columnState: "State",
  columnWhen: "When",
  noTasksTitle: "No tasks yet",
  noTasksDescription: "Give this agent its first task above, now or on a schedule.",
  taskQueued: "Queued",
  taskRunning: "Running",
  taskCompleted: "Completed",
  taskFailed: "Failed",
  taskStopped: "Stopped",
  taskScheduledState: "Scheduled",
  scheduleAt: "{repeat} at {time}",
  scheduleCustom: "On its own schedule ({cron})",
  scheduleLine: "{schedule} · {when}",
  nextRunAt: "Next on {when}",
  givenAt: "Given {when}",
  startedAt: "Started {when}",
  endedAt: "Ended {when}",
  fromSchedule: "From a schedule",
  stepsTitle: "Steps",
  stepsLabel: "What the agent did",
  stepsEmptyTitle: "No steps yet",
  stepsEmptyDescription: "Its machine is waking. Each step shows here as the agent takes it.",
  stepsScheduleTitle: "Runs on a schedule",
  stepsScheduleDescription: "Each run shows in the list with its own steps.",
  pickTaskTitle: "Pick a task",
  pickTaskDescription: "Choose one in the list to see its steps.",
  stop: "Stop",
  stopping: "Stopping…",
  endSchedule: "End schedule",
  stopRequested: "Asked {name} to stop",
  scheduleEnded: "Schedule ended",
  taskStoppedToast: "Task stopped",
  couldNotStop: "Could not stop that task",
  eventKindStarted: "Started",
  eventKindMessage: "Message",
  eventKindToolCall: "Tool call",
  eventKindApiCall: "Call to {product}",
  eventKindToolResult: "Tool result",
  eventKindError: "Error",
  eventKindTurnEnded: "Turn ended",
  eventKindOutput: "Output",
  eventStarted: "The agent started on the task.",
  eventAnswered: "Answered {status}",
  eventRefused: "Refused with {status}",
  eventTurnCompleted: "Done. It goes back to sleep after a few idle minutes.",
  eventTurnFailed: "The turn ended before the task was done.",
  eventTurnStopped: "Stopped before it finished.",
  failureComputerTitle: "The machine did not start",
  failureComputerDescription: "We could not wake it. Give the task again in a minute.",
  failureUnreachableTitle: "The agent did not answer",
  failureUnreachableDescription: "Its machine woke, but the agent did not respond. Give the task again.",
  failureTurnTitle: "The task did not finish",
  failureTurnDescription: "A step was refused. The steps below show where it stopped.",
  failureLostTitle: "The connection was lost",
  failureLostDescription: "The agent stopped before it said how the task ended.",
  failureLoginMissingTitle: "Not signed in yet",
  failureLoginMissingDescription:
    "This agent runs on your own login, and you have not signed in. Sign in under About this agent, then give the task again.",
  failureAddonTitle: "This needs the Remote agent add-on",
  failureAddonDescription: "Its machine did not wake and nothing was charged. Get the add-on in the store.",
  failureHoursTitle: "No awake hours left this month",
  failureHoursDescription: "Its machine did not wake and nothing was charged. Buy an hours pack to go on.",
  failureAiCreditsTitle: "No AI credits left",
  failureAiCreditsDescription:
    "No AI credits are left for our model. Nothing more was charged. Buy some, then try again.",
  machineTitle: "Your machine",
  machineSize: "{vcpus} vCPU · {memory} GB memory · {disk} GB disk",
  machineInUse: "Awake, using {memory} GB of memory.",
  machineNoMemory: "Saved to disk. It uses no memory.",
  machineNotPlaced: "It is set up on our pool with its first task.",
  awakeMinutes: { one: "1 minute awake this month", other: "{count} minutes awake this month" },
  awakeLeft: "{left} left this month, packs included",
  durationHours: "{hours} h",
  durationMinutes: "{minutes} min",
  durationHoursMinutes: "{hours} h {minutes} min",
  machineAsleep: "Asleep",
  machineWaking: "Waking",
  machineRunning: "Running",
  machineSaving: "Saving",
  awakeThisMonth: "Awake this month",
  hoursUnit: "h",
  detailsTitle: "About this agent",
  detailRunsOn: "Runs on",
  detailHosting: "Hosting",
  detailCreated: "Created",
  detailStatus: "Status",
  agentActions: "Agent actions",
  rename: "Rename",
  renameTitle: "Rename {name}",
  save: "Save",
  agentRenamed: "Agent renamed",
  couldNotRename: "Could not rename that agent",
  delete: "Delete",
  deleteTitle: "Delete {name}?",
  deleteDescription: "Its record and its key are removed now, and it can no longer act for you.",
  agentDeleted: "Agent deleted",
  couldNotDelete: "Could not delete that agent",
  stepSiteLinkedIn: "LinkedIn",
  stepSiteInstagram: "Instagram",
  stepSiteWeb: "the web",
  eventKindBrowser: "Browser",
  eventBrowserOpen: "Opened a page",
  eventBrowserNavigate: "Loaded another page",
  eventBrowserClose: "Closed a tab",
  eventBrowserAskDone: "You pressed Done",
  eventBrowserRead: "Read the page",
  eventBrowserClick: "Clicked on the page",
  eventBrowserType: "Typed on the page",
  eventBrowserTabs: "Looked at its tabs",
  eventBrowserAsk: "Handed you the screen",
  eventBrowserAskSite: "Handed you the screen to sign in to {site}",
  eventBrowserWait: "Waited for you to press Done",
  eventBrowserSignInCall: "Asked to be signed in",
  eventBrowserSignedIn: "Signed in to {site}",
  eventBrowserSignInRefused: "The sign-in was refused",
  loginTitle: "Login",
  harnessNameClaude: "Claude",
  harnessNameCodex: "Codex",
  signInWithClaude: "Sign in with Claude",
  signInWithCodex: "Sign in with Codex",
  loginNeededClaude:
    "Sign in with your own Claude account. Your agents on Claude use it for their tasks, and only you can.",
  loginNeededCodex:
    "Sign in with your own ChatGPT account for Codex. Your agents on Codex use it for their tasks, and only you can.",
  loginRefusedUntil: "Until you sign in, each task you give this agent is refused.",
  drawerLoginHint: "You can create it now and sign in later. Until you do, its tasks are refused.",
  claudeStepOpen: "Open Claude's page and approve the sign-in.",
  openClaudePage: "Open Claude's page",
  claudePageAddress: "Or copy the page's address",
  claudeStepPaste: "Paste the code Claude shows",
  claudeCodePlaceholder: "code#state",
  finishSignIn: "Finish sign-in",
  signingIn: "Signing in…",
  codexStepOpen: "Open OpenAI's page and enter this code.",
  openOpenAiPage: "Open OpenAI's page",
  codexCodeLabel: "Your one-time code",
  copyCode: "Copy code",
  copyCodeLabel: "Copy the one-time code",
  codeCopied: "Copied",
  codexWaiting: "Waiting for you to approve it on OpenAI's page…",
  signInExpiresAt: "This sign-in ends at {time}.",
  cancelSignIn: "Cancel",
  startAgain: "Start again",
  signedIn: "Signed in",
  signedInAs: "Signed in as {account}",
  loginSignedInOn: "Signed in {date}",
  loginValidUntil: "Valid until {date}",
  loginRefreshedOn: "Refreshed {date}",
  signInAgain: "Sign in again",
  removeLogin: "Remove",
  loginSaved: "Signed in with {harness}",
  loginSavedDescription: "Your agents on {harness} will use it for their next task.",
  loginRemoved: "{harness} login removed",
  couldNotSignIn: "Could not sign in",
  couldNotRemoveLogin: "Could not remove that login",
  noLoginNeeded: "Our model needs no login of yours.",
} as const;

const ES: Translation<AgentCatalog> = {
  screenTitleList: "Agentes · {product}",
  screenTitleAgent: "Agente · {product}",
  navAgents: "Agentes",
  breadcrumbHome: "Inicio",
  agentsTitle: "Agentes",
  agentsDescription:
    "Agentes que buscan prospectos por ti en LinkedIn e Instagram, en una máquina que operamos.",
  newRemoteAgent: "Nuevo agente remoto",
  tableLabel: "Tus agentes",
  columnAgent: "Agente",
  columnStatus: "Estado",
  columnLastTask: "Última tarea",
  noAgentsTitle: "Aún no hay agentes",
  noAgentsDescription: "Crea un agente remoto. Queda dormido hasta que le des una tarea.",
  noTaskYet: "Sin tareas aún",
  hostingRemote: "Remoto",
  agentKind: "{hosting} · {harness}",
  harnessClaudeCode: "Tu Claude",
  harnessCodex: "Tu Codex",
  harnessZaistevModel: "Nuestro modelo",
  harnessClaudeCodeDescription:
    "Usa tu propia sesión de Claude. El modelo no te cuesta nada de nuestra parte.",
  harnessCodexDescription: "Usa tu propia sesión de Codex. El modelo no te cuesta nada de nuestra parte.",
  harnessZaistevModelDescription: "Usa nuestro modelo, sin sesión tuya. Cada turno gasta tus créditos de IA.",
  eventKindModel: "Nuestro modelo",
  eventModelAnswered: "Consultó a nuestro modelo",
  eventModelRefused: "Nuestro modelo rechazó la llamada",
  statusAsleep: "Dormido",
  statusWaking: "Despertando",
  statusRunning: "Trabajando",
  statusIdle: "En espera",
  statusStopped: "Detenido",
  drawerTitle: "Nuevo agente remoto",
  drawerDescription: "Guardamos su ficha ahora. Su máquina solo despierta cuando le das una tarea.",
  name: "Nombre",
  namePlaceholder: "Scout",
  nameProblem: "Ponle un nombre de hasta 60 caracteres.",
  runsOn: "Funciona con",
  ownLoginsOnly: "Nuestro propio modelo aparecerá aquí cuando esté disponible.",
  create: "Crear agente",
  creating: "Creando…",
  cancel: "Cancelar",
  agentCreated: "{name} está listo",
  agentCreatedDescription: "Queda dormido hasta que le des una tarea.",
  addonRequiredTitle: "Los agentes remotos vienen con el complemento Agente remoto",
  addonRequiredDescription:
    "Le da a tu agente una máquina y {hours} horas encendida al mes. Consíguelo en la tienda y luego crea tu agente aquí.",
  getRemoteAgent: "Conseguir Agente remoto",
  couldNotCreate: "No pudimos crear ese agente",
  tasksTitle: "Tareas",
  tasksDescription: "Lo que se le pidió a este agente y cada paso que da.",
  giveTitle: "Dale una tarea",
  giveDescription: "Di qué hacer en una frase. El agente despierta, la hace y vuelve a dormir.",
  instructionLabel: "Tarea",
  instructionPlaceholder: "Busca 20 fundadores de SaaS en Madrid y agrégalos a Q4",
  instructionProblem: "Escribe la tarea en hasta 2000 caracteres.",
  whenLabel: "Cuándo",
  whenNow: "Ahora",
  whenSchedule: "Con horario",
  repeatLabel: "Se repite",
  repeatDaily: "Todos los días",
  repeatWeekdays: "De lunes a viernes",
  repeatMonday: "Cada lunes",
  repeatTuesday: "Cada martes",
  repeatWednesday: "Cada miércoles",
  repeatThursday: "Cada jueves",
  repeatFriday: "Cada viernes",
  repeatSaturday: "Cada sábado",
  repeatSunday: "Cada domingo",
  timeLabel: "A las",
  timeProblem: "Elige una hora como 09:00.",
  timeZoneHint: "En la zona horaria de tus preferencias.",
  giveTask: "Dar tarea",
  giving: "Dando…",
  scheduleTask: "Programar tarea",
  scheduling: "Programando…",
  taskGiven: "{name} ya está en ello",
  taskGivenDescription: "Su máquina despierta y cada paso aparece abajo mientras ocurre.",
  taskScheduledToast: "Tarea programada",
  taskScheduledToastDescription: "La próxima vez será el {when}.",
  couldNotGive: "No pudimos dar esa tarea",
  couldNotSchedule: "No pudimos programar esa tarea",
  poolBusyTitle: "El pool está ocupado",
  poolBusyDescription: "Todas nuestras máquinas están despiertas ahora. Da la tarea de nuevo en un minuto.",
  tasksTableLabel: "Las tareas de este agente",
  columnTask: "Tarea",
  columnState: "Estado",
  columnWhen: "Cuándo",
  noTasksTitle: "Aún no hay tareas",
  noTasksDescription: "Dale a este agente su primera tarea arriba, ahora o con horario.",
  taskQueued: "En cola",
  taskRunning: "Trabajando",
  taskCompleted: "Completada",
  taskFailed: "Falló",
  taskStopped: "Detenida",
  taskScheduledState: "Programada",
  scheduleAt: "{repeat} a las {time}",
  scheduleCustom: "Con su propio horario ({cron})",
  scheduleLine: "{schedule} · {when}",
  nextRunAt: "La próxima el {when}",
  givenAt: "Dada el {when}",
  startedAt: "Empezó el {when}",
  endedAt: "Terminó el {when}",
  fromSchedule: "De un horario",
  stepsTitle: "Pasos",
  stepsLabel: "Lo que hizo el agente",
  stepsEmptyTitle: "Aún no hay pasos",
  stepsEmptyDescription: "Su máquina está despertando. Cada paso aparece aquí cuando el agente lo da.",
  stepsScheduleTitle: "Se ejecuta con horario",
  stepsScheduleDescription: "Cada ejecución aparece en la lista con sus propios pasos.",
  pickTaskTitle: "Elige una tarea",
  pickTaskDescription: "Elige una de la lista para ver sus pasos.",
  stop: "Detener",
  stopping: "Deteniendo…",
  endSchedule: "Terminar horario",
  stopRequested: "Le pedimos a {name} que se detenga",
  scheduleEnded: "Horario terminado",
  taskStoppedToast: "Tarea detenida",
  couldNotStop: "No pudimos detener esa tarea",
  eventKindStarted: "Empezó",
  eventKindMessage: "Mensaje",
  eventKindToolCall: "Herramienta",
  eventKindApiCall: "Llamada a {product}",
  eventKindToolResult: "Resultado",
  eventKindError: "Error",
  eventKindTurnEnded: "Fin del turno",
  eventKindOutput: "Salida",
  eventStarted: "El agente empezó la tarea.",
  eventAnswered: "Respondió {status}",
  eventRefused: "Rechazada con {status}",
  eventTurnCompleted: "Listo. Vuelve a dormir tras unos minutos sin trabajo.",
  eventTurnFailed: "El turno terminó antes de completar la tarea.",
  eventTurnStopped: "Se detuvo antes de terminar.",
  failureComputerTitle: "La máquina no arrancó",
  failureComputerDescription: "No pudimos despertarla. Da la tarea de nuevo en un minuto.",
  failureUnreachableTitle: "El agente no respondió",
  failureUnreachableDescription: "Su máquina despertó, pero el agente no respondió. Da la tarea de nuevo.",
  failureTurnTitle: "La tarea no terminó",
  failureTurnDescription: "Se rechazó un paso. Los pasos de abajo muestran dónde se detuvo.",
  failureLostTitle: "Se perdió la conexión",
  failureLostDescription: "El agente se detuvo antes de decir cómo terminó la tarea.",
  failureLoginMissingTitle: "Aún no has iniciado sesión",
  failureLoginMissingDescription:
    "Este agente usa tu propia sesión y aún no has iniciado sesión. Hazlo en Sobre este agente y vuelve a darle la tarea.",
  failureAddonTitle: "Esto necesita el complemento Agente remoto",
  failureAddonDescription: "Su máquina no despertó y no se cobró nada. Consigue el complemento en la tienda.",
  failureHoursTitle: "No quedan horas de máquina este mes",
  failureHoursDescription:
    "Su máquina no despertó y no se cobró nada. Compra un paquete de horas para seguir.",
  failureAiCreditsTitle: "No quedan créditos de IA",
  failureAiCreditsDescription:
    "Nuestro modelo necesita créditos de IA y no quedan. No se cobró nada más. Compra créditos de IA y vuelve a dar la tarea.",
  machineTitle: "Tu máquina",
  machineSize: "{vcpus} vCPU · {memory} GB de memoria · {disk} GB de disco",
  machineInUse: "Encendida, usa {memory} GB de memoria.",
  machineNoMemory: "Guardada en disco. No usa memoria.",
  machineNotPlaced: "Se prepara en nuestro pool con su primera tarea.",
  awakeMinutes: { one: "1 minuto encendida este mes", other: "{count} minutos encendida este mes" },
  awakeLeft: "Quedan {left} este mes, con los paquetes",
  durationHours: "{hours} h",
  durationMinutes: "{minutes} min",
  durationHoursMinutes: "{hours} h {minutes} min",
  machineAsleep: "Dormida",
  machineWaking: "Despertando",
  machineRunning: "Encendida",
  machineSaving: "Guardando",
  awakeThisMonth: "Encendida este mes",
  hoursUnit: "h",
  detailsTitle: "Sobre este agente",
  detailRunsOn: "Funciona con",
  detailHosting: "Alojamiento",
  detailCreated: "Creado",
  detailStatus: "Estado",
  agentActions: "Acciones del agente",
  rename: "Renombrar",
  renameTitle: "Renombrar a {name}",
  save: "Guardar",
  agentRenamed: "Agente renombrado",
  couldNotRename: "No pudimos renombrar ese agente",
  delete: "Eliminar",
  deleteTitle: "¿Eliminar a {name}?",
  deleteDescription: "Su ficha y su clave se borran ahora, y ya no podrá actuar por ti.",
  agentDeleted: "Agente eliminado",
  couldNotDelete: "No pudimos eliminar ese agente",
  stepSiteLinkedIn: "LinkedIn",
  stepSiteInstagram: "Instagram",
  stepSiteWeb: "la web",
  eventKindBrowser: "Navegador",
  eventBrowserOpen: "Abrió una página",
  eventBrowserNavigate: "Cargó otra página",
  eventBrowserClose: "Cerró una pestaña",
  eventBrowserAskDone: "Pulsaste Listo",
  eventBrowserRead: "Leyó la página",
  eventBrowserClick: "Hizo clic en la página",
  eventBrowserType: "Escribió en la página",
  eventBrowserTabs: "Miró sus pestañas",
  eventBrowserAsk: "Te pasó la pantalla",
  eventBrowserAskSite: "Te pasó la pantalla para iniciar sesión en {site}",
  eventBrowserWait: "Esperó a que pulsaras Listo",
  eventBrowserSignInCall: "Pidió que inicien su sesión",
  eventBrowserSignedIn: "Sesión iniciada en {site}",
  eventBrowserSignInRefused: "Se rechazó el inicio de sesión",
  loginTitle: "Sesión",
  harnessNameClaude: "Claude",
  harnessNameCodex: "Codex",
  signInWithClaude: "Iniciar sesión con Claude",
  signInWithCodex: "Iniciar sesión con Codex",
  loginNeededClaude:
    "Inicia sesión con tu propia cuenta de Claude. Tus agentes con Claude la usan para sus tareas, y solo tú puedes.",
  loginNeededCodex:
    "Inicia sesión con tu propia cuenta de ChatGPT para Codex. Tus agentes con Codex la usan para sus tareas, y solo tú puedes.",
  loginRefusedUntil: "Hasta que inicies sesión, cada tarea que le des a este agente se rechaza.",
  drawerLoginHint: "Puedes crearlo ahora e iniciar sesión después. Mientras tanto, sus tareas se rechazan.",
  claudeStepOpen: "Abre la página de Claude y aprueba el inicio de sesión.",
  openClaudePage: "Abrir la página de Claude",
  claudePageAddress: "O copia la dirección de la página",
  claudeStepPaste: "Pega el código que muestra Claude",
  claudeCodePlaceholder: "código#estado",
  finishSignIn: "Terminar el inicio de sesión",
  signingIn: "Iniciando sesión…",
  codexStepOpen: "Abre la página de OpenAI e introduce este código.",
  openOpenAiPage: "Abrir la página de OpenAI",
  codexCodeLabel: "Tu código de un solo uso",
  copyCode: "Copiar código",
  copyCodeLabel: "Copiar el código de un solo uso",
  codeCopied: "Copiado",
  codexWaiting: "Esperando a que lo apruebes en la página de OpenAI…",
  signInExpiresAt: "Este inicio de sesión termina a las {time}.",
  cancelSignIn: "Cancelar",
  startAgain: "Empezar de nuevo",
  signedIn: "Sesión iniciada",
  signedInAs: "Sesión iniciada como {account}",
  loginSignedInOn: "Sesión iniciada el {date}",
  loginValidUntil: "Válida hasta el {date}",
  loginRefreshedOn: "Renovada el {date}",
  signInAgain: "Iniciar sesión de nuevo",
  removeLogin: "Quitar",
  loginSaved: "Sesión iniciada con {harness}",
  loginSavedDescription: "Tus agentes con {harness} la usarán en su próxima tarea.",
  loginRemoved: "Sesión de {harness} quitada",
  couldNotSignIn: "No pudimos iniciar sesión",
  couldNotRemoveLogin: "No pudimos quitar esa sesión",
  noLoginNeeded: "Nuestro modelo no necesita ninguna sesión tuya.",
};

export const AGENT_CATALOGS: Catalogs<AgentCatalog> = { en: EN, es: ES };
